import React, { useState, useMemo, useCallback, useEffect } from 'react';

import { Iframe, getModalDimensions } from '../../custom-ui';
import {
	type ModalIframeRendererProps,
	type CustomUIRendererProps,
	type InternalModalState,
} from './types';
import Modal, { ModalHeader, ModalTitle, ModalTransition } from '@atlaskit/modal-dialog';
import { FullScreenModalDialog } from '@atlaskit/modal-dialog/full-screen';
import { cssMap } from '@atlaskit/css';
import { useIsReducedMotion } from '@atlaskit/motion/accessibility';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { JiraExtension } from '@atlassian/forge-ui-types';
import { getAtlasKitModalWidth, ModalContentWrapper } from './custom-ui-modal-utils';
import { ForgeModalDeprecationMessage } from '../ForgeModalDeprecationMessage';
import { ForgeDeprecationMessage, SANDBOX_DEPRECATION_ERROR } from '../ForgeDeprecationMessage';
import { IconButton } from '@atlaskit/button/new';
import CrossIcon from '@atlaskit/icon/core/cross';

const styles = cssMap({
	modalDeprecationMessage: {
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
});

type Props = CustomUIRendererProps & {
	isInModal?: boolean;
	// These are internal props, not exposed to the product
	onModalCustomUIRendererClose?: () => Promise<void>;
	setIsLoading?: React.Dispatch<React.SetStateAction<boolean>>;
};

export const CustomUIRenderer = ({
	accountId,
	bridge,
	client,
	contextIds,
	coreData,
	customBridgeMethods,
	dialogs,
	entryPoint,
	extension,
	extensionData,
	extensionPayload,
	extensionViewData,
	getContextToken,
	getThreeLOPrompt,
	height,
	width,
	isIframeHidden,
	isIframeResizable,
	iframeRef,
	isInModal,
	loadingComponent,
	locale,
	localId,
	modalStyles,
	modalProvider,
	onIframeLoad,
	onModalCustomUIRendererClose,
	product,
	timezone,
	getModalWidth,
	setIsLoading,
	isEmbeddedMacro,
	embeddedParentLocalId,
}: Props): React.JSX.Element => {
	const [error, setError] = useState<string | null>(null);
	// modalOptions is null when no modal is open is non null when opened
	// Uses InternalModalState to allow extension property from FAUS forwarding
	const [modalOptions, setModalOptions] = useState<InternalModalState | null>(null);
	const [viewOnClose, setViewOnClose] = useState<() => Promise<void> | void>();

	const onCloseFromModalClass = modalOptions?.onClose;

	const iframeProps = {
		accountId,
		apolloClient: client,
		contextIds,
		components: getThreeLOPrompt,
		coreData,
		customBridgeMethods,
		extension,
		extensionData,
		extensionPayload,
		extensionViewData,
		getContextToken,
		isHidden: isIframeHidden,
		isResizable: isIframeResizable,
		locale,
		timezone,
		loadingComponent,
		isEmbeddedMacro,
		embeddedParentLocalId,
	};

	const platformAddBridgeMessageForwardingFromFaus = fg(
		'platform_add_bridge_message_forwarding_from_faus',
	);

	const extensionInModal = useMemo(() => {
		if (platformAddBridgeMessageForwardingFromFaus) {
			// Use the embedded macro's extension if provided (for modals opened from embedded macros)
			const baseExtension = modalOptions?.extension || extension;
			return {
				...baseExtension,
				// Pass a type that is not included in the dimensions presets so that it falls back to default
				type: `${baseExtension.type}::dynamic-modal`,
				properties: {
					...baseExtension.properties,
					resource: modalOptions?.resource ?? baseExtension.properties.resource,
				},
			};
		}

		return {
			...extension,
			// Pass a type that is not included in the dimensions presets so that it falls back to default
			type: `${extension.type}::dynamic-modal`,
			properties: {
				...extension.properties,
				resource: modalOptions?.resource ?? extension.properties.resource,
			},
		};
	}, [
		extension,
		modalOptions?.resource,
		modalOptions?.extension,
		platformAddBridgeMessageForwardingFromFaus,
	]);

	const onError = (error: string | undefined) => {
		error && setError(error);
	};

	const extensionDataInModal = useMemo(
		() => ({
			...extensionData,
			...(modalOptions?.context ? { modal: modalOptions?.context } : {}),
			// This is to handle an incompatibility issue with Compass and JSM where they are incorrectly currently spreading the context object on the top level instead of in the `modal` object.
			// Remove once Compass apps have been updated https://softwareteams.atlassian.net/browse/COMPASS-22051 and a notice to app developers have been sent out
			...((product === 'compass' || extension.type.startsWith('jiraServiceManagement')) &&
			modalOptions?.context
				? { ...modalOptions?.context }
				: {}),
		}),
		[extension.type, extensionData, modalOptions?.context, product],
	);

	const handleModalTearDown = useCallback(() => {
		setModalOptions(null);
	}, [setModalOptions]);

	const isReducedMotion = useIsReducedMotion();

	const handleModalClose = useCallback(() => {
		onCloseFromModalClass?.();
		// if reduced motion is enabled, we need to call handleModalTearDown here as it no longer calls `onCloseComplete` in `ModalCustomUIRenderer`
		if (isReducedMotion) {
			handleModalTearDown();
		}
	}, [isReducedMotion, onCloseFromModalClass, handleModalTearDown]);

	// Confluence specific feature
	// Needed to show Forge modals in live pages. dialogs won't be passed in if the page isn't live.
	useEffect(() => {
		if (product !== 'confluence') {
			return;
		}
		if (dialogs && modalOptions) {
			dialogs.showDialog(ModalCustomUIRenderer, {
				extension: extensionInModal,
				extensionData: extensionDataInModal,
				localId,
				onTearDown: handleModalTearDown,
				bridge,
				size: modalOptions.size,
				closeOnEscape: modalOptions.closeOnEscape,
				closeOnOverlayClick: modalOptions.closeOnOverlayClick,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [modalOptions, dialogs]);

	if (error) {
		// Sandbox deprecation error
		if (error === SANDBOX_DEPRECATION_ERROR) {
			return <ForgeDeprecationMessage extension={extension} client={client} error={error} />;
		}

		// Currently no other error types are handled
	}

	return (
		<>
			{/* isResizable defaults to true within Iframe */}
			<Iframe
				{...iframeProps}
				entryPoint={entryPoint}
				isResizable={isIframeResizable}
				bridge={{
					...bridge,
					openModal: (opts) => {
						setModalOptions(opts.data);
						return true;
					},
					...(onModalCustomUIRendererClose
						? {
								close: (payload: any) => {
									if (fg('forge-ui-on-close-bridge-method')) {
										onModalCustomUIRendererClose()
											.then(() => {
												bridge?.close?.(payload);
											})
											.finally(() => {
												return true;
											});
									} else {
										// This bridge.close reference is defined below in ModalCustomUIRenderer.
										onModalCustomUIRendererClose();
										bridge?.close?.(payload);
										return true;
									}
								},
							}
						: {}),
				}}
				height={height}
				width={width}
				isInModal={isInModal}
				ref={iframeRef}
				onLoad={onIframeLoad}
				setIsLoading={setIsLoading}
				onError={onError}
			/>
			{modalOptions ? (
				<ModalCustomUIRenderer
					{...iframeProps}
					product={product}
					getThreeLOPrompt={getThreeLOPrompt}
					dialogs={dialogs}
					client={client}
					extension={extensionInModal}
					extensionData={extensionDataInModal}
					localId={localId}
					onTearDown={handleModalTearDown}
					bridge={{
						...bridge,
						onClose: (callback) => {
							setViewOnClose(() => callback.data);
							// stores the callback for when modal is closed
							return true;
						},
						close: (payload: any) => {
							onCloseFromModalClass?.(payload.data);
							return true;
						},
					}}
					modalOnClose={handleModalClose}
					size={modalOptions.size}
					closeOnEscape={modalOptions.closeOnEscape}
					closeOnOverlayClick={modalOptions.closeOnOverlayClick}
					modalStyles={modalStyles}
					modalProvider={modalProvider}
					getModalWidth={getModalWidth}
					isIframeResizable={isIframeResizable}
					viewOnClose={viewOnClose}
				/>
			) : null}
		</>
	);
};

export const ModalCustomUIRenderer = ({
	accountId,
	bridge,
	coreData,
	client,
	closeOnEscape,
	closeOnOverlayClick,
	consentMessage,
	contextIds,
	customBridgeMethods,
	dialogs,
	entryPoint,
	extension,
	extensionData,
	extensionPayload,
	extensionViewData,
	getContextToken,
	getThreeLOPrompt,
	isIframeHidden = false,
	isIframeResizable = false,
	loadingComponent,
	locale,
	localId,
	mentionProvider,
	modalExtension,
	modalStyles,
	modalProvider: ModalProvider,
	onConsentModalClose,
	onConsentSuccess,
	onError,
	onForgeDocUpdated,
	onIframeLoad,
	onTearDown,
	product,
	size,
	timezone,
	getModalWidth,
	// onClose from `Modal`
	modalOnClose,
	// onClose from `view`
	viewOnClose,
}: ModalIframeRendererProps): React.JSX.Element => {
	const [isOpen, setIsOpen] = useState(true);

	// This is for preventing inline dialog apps from being closed by an esc key press when a
	// modal is open until https://product-fabric.atlassian.net/browse/DSP-3552 is resolved.
	useEffect(() => {
		if (
			product !== 'confluence' ||
			modalExtension?.type === 'macroConfig' ||
			(product === 'confluence' && modalExtension?.type === 'bylineItem')
		) {
			return;
		}
		function dismissEscape(e: any) {
			if (e.key === 'Escape') {
				e.stopPropagation();

				if (closeOnEscape) {
					modalOnClose?.();
					setIsOpen(false);
				}
			}
		}

		if (isOpen) {
			document.addEventListener('keydown', dismissEscape, { capture: true });
		}

		return () =>
			document.removeEventListener('keydown', dismissEscape, {
				capture: true,
			});
	}, [isOpen, closeOnEscape, product, modalOnClose, modalExtension?.type]);

	const modalSize = size ?? extension.properties.viewportSize ?? 'medium';
	const modalWidth = getModalWidth ? getModalWidth(size) : getAtlasKitModalWidth(modalSize);

	// If size is defined, iframe modal should not be resizable.
	// In JSM ONLY, iframe modal should ALWAYS be resizable.
	// Iframe should scale to 100% of the modal so that Select drop downs are not cut off
	const isJsmModule = extension.type.startsWith('jiraServiceManagement');
	const isModalResizable = isJsmModule || (size !== undefined ? false : isIframeResizable);

	const shouldShowFCFModalDeprecationMessage =
		(extension.type === JiraExtension.CUSTOM_FIELD ||
			extension.type === JiraExtension.CUSTOM_FIELD_TYPE) &&
		!fg('forge_disable_fcf_modal_experience_on_issue_view');

	const renderModal = () => {
		const closeProps = {
			onClose: async () => {
				if (fg('forge-ui-on-close-bridge-method')) {
					try {
						await viewOnClose?.();
					} finally {
						modalOnClose?.();
						setIsOpen(false);
					}
				} else {
					modalOnClose?.();
					setIsOpen(false);
				}
			},
			onCloseComplete: onTearDown,
		};

		const modalContent = (
			<>
				{shouldShowFCFModalDeprecationMessage && (
					<Box xcss={styles.modalDeprecationMessage}>
						<ForgeModalDeprecationMessage client={client} extension={extension} />
					</Box>
				)}
				<ModalContentWrapper size={modalSize} styles={modalStyles} isResizable={isIframeResizable}>
					<CustomUIRenderer
						product={product}
						coreData={coreData}
						getThreeLOPrompt={getThreeLOPrompt}
						dialogs={dialogs}
						accountId={accountId}
						client={client}
						contextIds={contextIds}
						extension={extension}
						extensionData={extensionData}
						extensionViewData={extensionViewData}
						bridge={bridge}
						customBridgeMethods={customBridgeMethods}
						consentMessage={consentMessage}
						entryPoint={entryPoint}
						getContextToken={getContextToken}
						height="100%"
						isIframeHidden={isIframeHidden}
						isIframeResizable={isModalResizable}
						locale={locale}
						localId={localId}
						mentionProvider={mentionProvider}
						modalExtension={modalExtension}
						onConsentSuccess={onConsentSuccess}
						onForgeDocUpdated={onForgeDocUpdated}
						timezone={timezone}
						onConsentModalClose={onConsentModalClose}
						onError={onError}
						onIframeLoad={onIframeLoad}
						loadingComponent={loadingComponent}
						extensionPayload={extensionPayload}
						modalStyles={modalStyles}
						// This is called when `view.close` is called
						onModalCustomUIRendererClose={async () => {
							if (viewOnClose && fg('forge-ui-on-close-bridge-method')) {
								try {
									await viewOnClose?.();
								} finally {
									setIsOpen(false);
									// If is a modal extension point, call modalOnClose to reset state from product
									// This is required to reset the state of the modal in the product when view.close is called
									modalExtension ? modalOnClose?.() : null;
								}
							} else {
								setIsOpen(false);
								// if is a modal extension point, call modalOnClose to reset state from product
								// This is required to reset the state of the modal in the product when view.close is called
								modalExtension ? modalOnClose?.() : null;
							}
						}}
						isInModal={true}
					/>
				</ModalContentWrapper>
			</>
		);

		if (modalSize === 'fullscreen' && fg('platform_add_modal_support_to_bylineitem_extension')) {
			return (
				<FullScreenModalDialog
					{...closeProps}
					testId="custom-ui-fullscreen-modal-dialog"
					shouldCloseOnEscapePress={closeOnEscape}
				>
					<ModalHeader>
						<ModalTitle>{modalExtension?.title}</ModalTitle>
						<IconButton
							appearance="subtle"
							icon={CrossIcon}
							label="Close Modal"
							onClick={modalExtension?.closeModalExtension}
						/>
					</ModalHeader>
					{modalContent}
				</FullScreenModalDialog>
			);
		} else {
			const { height: modalHeight } = getModalDimensions(modalSize ?? 'medium');

			return (
				<Modal
					{...closeProps}
					width={modalWidth}
					height={modalSize === 'max' ? modalHeight : undefined}
					testId="custom-ui-modal-dialog"
					shouldCloseOnOverlayClick={closeOnOverlayClick}
					shouldCloseOnEscapePress={closeOnEscape}
				>
					{modalContent}
				</Modal>
			);
		}
	};

	if (ModalProvider) {
		return (
			<ModalTransition>{isOpen && <ModalProvider>{renderModal()}</ModalProvider>}</ModalTransition>
		);
	}

	return <ModalTransition>{isOpen && renderModal()}</ModalTransition>;
};
